import React, { ReactElement, isValidElement, useEffect, useRef, useState } from 'react';
import Flickity from 'react-flickity-component';
import { useShopifyProduct } from '../../../../../api/shopify/ShopifyProduct';
import Image from '../../../common/Image';
import { styles } from './Slider.css';

export type SliderProps = {
  arrows?: boolean;
  className?: string;
  sortedImages: Array<string | ReactElement>;
  title: string;
  initialSlide?: number;
  imageWidth?: number;
  imageHeight?: number;
  imageSizes?: string;
};

export const Slider = ({
  arrows = false,
  className,
  sortedImages,
  title,
  initialSlide = 0,
  imageWidth = 800,
  imageHeight,
  imageSizes = '100vw',
}: SliderProps): JSX.Element => {
  const { shopify } = useShopifyProduct();
  const sliderRef = useRef<any>();
  const [flickityReady, setFlickityReady] = useState<boolean>(false);
  const [currentSlide, setCurrentSlide] = useState<number>(initialSlide + 1);
  const hasMultiple = sortedImages.length > 1;

  useEffect(() => {
    sliderRef.current.on('change', setSlide);
    sliderRef.current.on('ready', setReady);

    return () => {
      sliderRef.current.off('change', setSlide);
      sliderRef.current.off('ready', setReady);
      setCurrentSlide(1);
    };
  }, [sortedImages]);

  const setReady = () => {
    const sliderElement = sliderRef.current;
    sliderElement?.focus();
    setFlickityReady(true);
  };

  const setSlide = (slideNum: number) => {
    setCurrentSlide(slideNum + 1);
  };

  return (
    <div className={[styles.root, hasMultiple ? styles.hasMultiple : null].join(' ')}>
      <Flickity
        key={`${shopify.id}-${shopify.title}-flickity`}
        flickityRef={ref => (sliderRef.current = ref)}
        className={[flickityReady ? 'flickity-ready flickity-enabled' : null, styles.carousel, className].join(' ')}
        elementType={'div'}
        options={{
          adaptiveHeight: false,
          prevNextButtons: arrows,
          initialIndex: initialSlide,
          pageDots: hasMultiple,
          lazyLoad: true,
        }}
        disableImagesLoaded={false}
      >
        {sortedImages.map((image, index) => (
          <div key={index} className={styles.slide}>
            {isValidElement(image) ? (
              image
            ) : (
              <Image
                src={image as string}
                width={imageWidth}
                height={imageHeight}
                sizes={imageSizes}
                alt={title}
                lazyload={index !== initialSlide}
                flickityLazyload={index !== initialSlide}
              />
            )}
          </div>
        ))}
      </Flickity>
      {hasMultiple ? (
        <div className={styles.pageNums}>
          {currentSlide} of {sortedImages.length} images
        </div>
      ) : null}
    </div>
  );
};
